import type {
  Forecast,
  ForecastDetail,
  ForecastPage,
  ForecastSummary,
  ForecastUtility,
  ForecastWatchPage,
} from '../consumer/forecasting';
type Validate = (value: unknown) => void;
const invalid = (): never => {
  throw new Error('Invalid NormalMonitoringForecastV1 response');
};
const text: Validate = (v) => {
  if (typeof v !== 'string' || v.length > 16384) invalid();
};
const id: Validate = (v) => {
  text(v);
  if (!v) invalid();
};
const finite: Validate = (v) => {
  if (typeof v !== 'number' || !Number.isFinite(v)) invalid();
};
const count: Validate = (v) => {
  finite(v);
  if (!Number.isSafeInteger(v) || (v as number) < 0) invalid();
};
const bool: Validate = (v) => {
  if (typeof v !== 'boolean') invalid();
};
const time: Validate = (v) => {
  id(v);
  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(v as string) ||
    !Number.isFinite(Date.parse(v as string))
  )
    invalid();
};
const nullable =
  (validate: Validate): Validate =>
  (v) => {
    if (v !== null) validate(v);
  };
const one =
  (...values: unknown[]): Validate =>
  (v) => {
    if (!values.includes(v)) invalid();
  };
const array =
  (validate: Validate, max = 10000): Validate =>
  (v) => {
    if (!Array.isArray(v) || v.length > max) invalid();
    (v as unknown[]).forEach(validate);
  };
const strings = array(text);
const record =
  (shape: Record<string, Validate>): Validate =>
  (v) => {
    if (!v || typeof v !== 'object' || Array.isArray(v)) invalid();
    const obj = v as Record<string, unknown>;
    if (
      Object.keys(obj).length !== Object.keys(shape).length ||
      Object.keys(obj).some((key) => !Object.hasOwn(shape, key))
    )
      invalid();
    Object.entries(shape).forEach(([key, validate]) => validate(obj[key]));
  };
const forecastId: Validate = (v) => {
  id(v);
  if (!/^forecast-[a-f0-9]{64}$/.test(v as string)) invalid();
};
const horizon = one(5, 10, 20);
const metric = one('TEMPERATURE', 'PARTICULATE');
const profile = one('FORECAST_SYNTHETIC_V1');
const sample = record({
  reference: id,
  observedAt: time,
  receivedAt: time,
  availableAt: time,
  availableSequence: count,
  value: finite,
  instanceId: id,
  areaId: id,
  sourceId: id,
  metric: id,
  unit: id,
  epoch: count,
  quality: id,
  unknowns: strings,
});
const point = record({
  horizonSeconds: horizon,
  targetAt: time,
  deadlineAt: time,
  localLinearValue: finite,
  lastValue: finite,
  threshold: finite,
  localLinearCrosses: bool,
  lastValueCrosses: bool,
});
const forecast = record({
  forecastId,
  instanceId: id,
  areaId: id,
  sourceId: id,
  metric,
  unit: id,
  epoch: count,
  configRevision: count,
  sourceRevision: count,
  origin: time,
  issuedAt: nullable(time),
  persistedAtUpperBound: nullable(time),
  inputWatermark: count,
  inputFingerprint: id,
  profileVersion: profile,
  algorithmVersion: one('LOCAL_LINEAR_V1'),
  benchmarkVersion: one('LAST_VALUE_V1'),
  mode: one('SYNTHETIC_ONLINE', 'OFFLINE_AS_OF_REPLAY'),
  publishStatus: one(
    'TIMELY',
    'PUBLICATION_UNCONFIRMED',
    'LATE_PUBLICATION',
    'MISSED_PUBLICATION',
    'ALGORITHM_FAILURE',
  ),
  sampleCount: count,
  spanSeconds: finite,
  intercept: nullable(finite),
  slopePerSecond: nullable(finite),
  latestValue: nullable(finite),
  inputs: array(sample, 10000),
  points: array(point, 3),
  limitations: strings,
});
const evaluation = record({
  forecastId,
  horizonSeconds: horizon,
  targetAt: time,
  deadlineAt: time,
  evaluatedAt: nullable(time),
  status: one('PENDING', 'EVALUATED', 'UNAVAILABLE'),
  reason: text,
  candidates: array(sample),
  actualRef: nullable(id),
  actualObservedAt: nullable(time),
  actualReceivedAt: nullable(time),
  actualAvailableAt: nullable(time),
  actualValue: nullable(finite),
  linearSignedError: nullable(finite),
  linearAbsoluteError: nullable(finite),
  linearSquaredError: nullable(finite),
  lastSignedError: nullable(finite),
  lastAbsoluteError: nullable(finite),
  lastSquaredError: nullable(finite),
  linearOutcome: nullable(one('TP', 'FP', 'FN', 'TN')),
  lastOutcome: nullable(one('TP', 'FP', 'FN', 'TN')),
});
const applicability = record({
  forecastId,
  status: one(
    'CURRENT',
    'SUPERSEDED',
    'EXPIRED',
    'SOURCE_DISABLED',
    'EPOCH_CHANGED',
    'CONFIGURATION_CHANGED',
  ),
  reason: text,
  changedAt: nullable(time),
});
const qualification = record({
  sourceId: id,
  areaId: id,
  metric: id,
  unit: id,
  epoch: count,
  enabled: bool,
  status: one('QUALIFIED', 'INELIGIBLE', 'NOT_FORECASTED', 'ALGORITHM_FAILURE'),
  reason: text,
  sampleCount: count,
  spanSeconds: finite,
  latestObservedAt: nullable(time),
  inputWatermark: count,
  latestForecastId: nullable(forecastId),
});
const watch = record({
  watchId: id,
  areaId: id,
  horizonSeconds: horizon,
  origin: time,
  targetAt: time,
  createdAt: time,
  updatedAt: time,
  status: one('ACTIVE', 'EXPIRED', 'WITHDRAWN'),
  forecastIds: array(forecastId, 1000),
  sourceIds: strings,
  reason: text,
  activeObservedWarningId: nullable(id),
  limitations: strings,
});
const summary = record({
  profileVersion: profile,
  generatedAt: time,
  eligibleSourceCount: count,
  forecastCount: count,
  forecastTaskCount: count,
  pendingForecastTaskCount: count,
  pendingEvaluationCount: count,
  evaluatedCount: count,
  unavailableCount: count,
  activeWatchCount: count,
  sources: array(qualification, 1000),
  watches: array(watch),
  limitations: strings,
});
const utilityGroup = record({
  metric,
  unit: id,
  horizonSeconds: horizon,
  algorithmVersion: one('LOCAL_LINEAR_V1', 'LAST_VALUE_V1'),
  profileVersion: profile,
  generated: count,
  timely: count,
  evaluable: count,
  unavailable: count,
  invalidated: count,
  mae: nullable(finite),
  rmse: nullable(finite),
  tp: count,
  fp: count,
  fn: count,
  tn: count,
  precision: nullable(finite),
  recall: nullable(finite),
});
const utility = record({
  generatedAt: time,
  predictiveUtility: id,
  groups: array(utilityGroup, 12),
  limitations: strings,
});
function validateForecast(f: Forecast) {
  if (
    f.unit !== (f.metric === 'TEMPERATURE' ? 'degC' : 'ug/m3') ||
    f.sampleCount !== f.inputs.length ||
    new Set(f.points.map((p) => p.horizonSeconds)).size !== f.points.length
  )
    invalid();
  const origin = Date.parse(f.origin);
  for (const s of f.inputs)
    if (
      s.instanceId !== f.instanceId ||
      s.areaId !== f.areaId ||
      s.sourceId !== f.sourceId ||
      s.metric !== f.metric ||
      s.unit !== f.unit ||
      s.epoch !== f.epoch ||
      Date.parse(s.observedAt) > origin ||
      Date.parse(s.receivedAt) > origin ||
      Date.parse(s.availableAt) > origin ||
      s.availableSequence > f.inputWatermark
    )
      invalid();
  for (const p of f.points)
    if (
      Date.parse(p.targetAt) !== origin + p.horizonSeconds * 1000 ||
      Date.parse(p.deadlineAt) !== Date.parse(p.targetAt) + 4000
    )
      invalid();
}
export function parseForecastSummary(v: unknown): ForecastSummary {
  summary(v);
  const parsed = v as ForecastSummary;
  if (parsed.forecastTaskCount !== parsed.forecastCount + parsed.pendingForecastTaskCount)
    invalid();
  return parsed;
}
export function parseForecastPage(v: unknown): ForecastPage {
  record({
    items: array(forecast, 100),
    total: count,
    offset: count,
    limit: count,
    generatedAt: time,
  })(v);
  const p = v as ForecastPage;
  if (
    p.limit < 1 ||
    p.limit > 100 ||
    p.offset > 1000000 ||
    p.items.length > p.limit ||
    p.items.length > p.total
  )
    invalid();
  p.items.forEach(validateForecast);
  return p;
}
export function parseForecastDetail(v: unknown): ForecastDetail {
  record({ forecast, applicability, evaluations: array(evaluation, 3) })(v);
  const d = v as ForecastDetail;
  validateForecast(d.forecast);
  if (
    d.applicability.forecastId !== d.forecast.forecastId ||
    new Set(d.evaluations.map((e) => e.horizonSeconds)).size !== d.evaluations.length
  )
    invalid();
  for (const e of d.evaluations) {
    const p = d.forecast.points.find((p) => p.horizonSeconds === e.horizonSeconds);
    if (
      e.forecastId !== d.forecast.forecastId ||
      !p ||
      e.targetAt !== p.targetAt ||
      e.deadlineAt !== p.deadlineAt
    )
      invalid();
  }
  return d;
}
export function parseForecastUtility(v: unknown): ForecastUtility {
  utility(v);
  const u = v as ForecastUtility;
  for (const g of u.groups)
    if (
      g.unit !== (g.metric === 'TEMPERATURE' ? 'degC' : 'ug/m3') ||
      g.evaluable > g.timely ||
      g.timely > g.generated
    )
      invalid();
  return u;
}
export function parseForecastWatchPage(v: unknown): ForecastWatchPage {
  record({
    items: array(record({ watch, revision: count }), 100),
    total: count,
    offset: count,
    limit: count,
    generatedAt: time,
  })(v);
  const page = v as ForecastWatchPage;
  if (
    page.limit < 1 ||
    page.limit > 100 ||
    page.offset > 1000000 ||
    page.items.length > page.total ||
    page.items.length > page.limit ||
    page.items.some((w) => w.revision < 1)
  )
    invalid();
  return page;
}
